import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { can } from "@/lib/helpers";
import { PageHeader } from "@/components/Common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, PencilSimple, ShieldCheck } from "@phosphor-icons/react";

export default function Roles() {
  const { user } = useAuth();
  const [roles, setRoles] = useState([]);
  const [catalog, setCatalog] = useState({});
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ name: "", description: "", permissions: [], max_discount_percent: 0 });
  const canCreate = can(user, "roles", "create");
  const canEdit = can(user, "roles", "edit");

  const load = async () => setRoles((await api.get("/roles")).data);
  useEffect(() => { load(); api.get("/permissions/catalog").then(({ data }) => setCatalog(data)); }, []);

  const openNew = () => { setForm({ name: "", description: "", permissions: [], max_discount_percent: 0 }); setEditId(null); setOpen(true); };
  const openEdit = (r) => {
    setForm({ name: r.name, description: r.description, permissions: r.permissions.includes("*") ? allPerms() : r.permissions, max_discount_percent: r.max_discount_percent });
    setEditId(r.id); setOpen(true);
  };
  const allPerms = () => Object.entries(catalog).flatMap(([res, acts]) => acts.map((a) => `${res}:${a}`));

  const toggle = (perm) => {
    setForm((f) => ({ ...f, permissions: f.permissions.includes(perm) ? f.permissions.filter((p) => p !== perm) : [...f.permissions, perm] }));
  };

  const submit = async () => {
    try {
      const payload = { ...form, max_discount_percent: Number(form.max_discount_percent) };
      if (editId) { await api.put(`/roles/${editId}`, payload); toast.success("Role updated"); }
      else { await api.post("/roles", payload); toast.success("Role created"); }
      setOpen(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="p-6 lg:p-8 fade-in">
      <PageHeader title="Roles & Permissions" subtitle="Granular permission matrix and discount approval limits.">
        {canCreate && <Button onClick={openNew} className="bg-[#C5A059] hover:bg-[#B38F4E] text-white" data-testid="add-role-btn"><Plus size={16} className="mr-1" /> New Role</Button>}
      </PageHeader>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {roles.map((r) => (
          <Card key={r.id} className="p-5 bg-white border-[#E6E2D6] shadow-sm" data-testid={`role-card-${r.name.replace(/\s/g, "-")}`}>
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-2">
                <ShieldCheck size={20} className="text-[#C5A059]" weight="duotone" />
                <div className="font-medium text-[#1A1A1A]">{r.name}</div>
              </div>
              {r.is_system && <Badge className="bg-[#F2EBE1] text-[#8A6D2F] border-0 text-[10px]">System</Badge>}
            </div>
            <p className="text-xs text-[#8A8A8A] mt-2 h-8">{r.description}</p>
            <div className="flex items-center justify-between mt-3 text-xs">
              <span className="text-[#4A4A4A]">{r.permissions.includes("*") ? "All permissions" : `${r.permissions.length} permissions`}</span>
              <span className="text-[#8A6D2F]">Disc ≤ {r.max_discount_percent}%</span>
            </div>
            {canEdit && <Button variant="outline" size="sm" onClick={() => openEdit(r)} className="w-full mt-3 border-[#E6E2D6]" data-testid={`edit-role-${r.name.replace(/\s/g, "-")}`}><PencilSimple size={14} className="mr-1" /> Edit Permissions</Button>}
          </Card>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-2xl">{editId ? "Edit Role" : "New Role"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3 mb-2">
            <div><Label className="text-xs text-[#6B6B6B]">Role Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 bg-white border-[#E6E2D6]" data-testid="rf-name" /></div>
            <div><Label className="text-xs text-[#6B6B6B]">Max Discount %</Label><Input type="number" value={form.max_discount_percent} onChange={(e) => setForm({ ...form, max_discount_percent: e.target.value })} className="mt-1 bg-white border-[#E6E2D6]" data-testid="rf-discount" /></div>
            <div className="col-span-2"><Label className="text-xs text-[#6B6B6B]">Description</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1 bg-white border-[#E6E2D6]" data-testid="rf-desc" /></div>
          </div>
          <div className="space-y-3">
            {Object.entries(catalog).map(([res, acts]) => (
              <div key={res} className="border border-[#E6E2D6] rounded-md p-3">
                <div className="text-xs font-semibold uppercase tracking-wider text-[#8A6D2F] mb-2">{res.replace("_", " ")}</div>
                <div className="flex flex-wrap gap-3">
                  {acts.map((a) => {
                    const perm = `${res}:${a}`;
                    return (
                      <label key={perm} className="flex items-center gap-1.5 text-sm cursor-pointer">
                        <Checkbox checked={form.permissions.includes(perm)} onCheckedChange={() => toggle(perm)} data-testid={`perm-${perm}`} />
                        <span className="text-[#4A4A4A]">{a.replace("_", " ")}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
          <DialogFooter><Button onClick={submit} className="bg-[#C5A059] hover:bg-[#B38F4E] text-white" data-testid="rf-save">{editId ? "Save Role" : "Create Role"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
